import type { AppraisalDetail } from "../appraisal-workflow/types.ts";
import { annualBenefitsFromBasic, esicEmployeeFromBasic, esicEmployerFromBasic, isEsicEligible, providentFundFromBasic } from "./appraisal-annexure-utils.ts";
import type { AppraisalAnnexureValues, AppraisalLetterType, EmployeeAppraisalValues } from "./types.ts";

const LETTER_TYPES: AppraisalLetterType[] = ["APPRAISAL", "PROMOTION", "APPRAISAL_WITH_PROMOTION"];

function amount(value: unknown): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function amountText(value: unknown): string {
  return String(amount(value));
}

function localDate(value?: string): Date | null {
  if (!value) return null;
  const [year, month, day] = value.slice(0, 10).split("-").map(Number);
  return year && month && day ? new Date(year, month - 1, day) : null;
}

function noticePeriodValue(value?: string): string {
  const normalized = value?.trim().match(/^(\d+)\s*days?$/i);
  return normalized ? `${normalized[1]} Days` : value?.trim() || "90 Days";
}

export type AppraisalFormRouteState = {
  rejectedAppraisal?: AppraisalDetail;
  draftAppraisal?: AppraisalDetail;
};

export function appraisalBasicValuesFromDetail(detail: AppraisalDetail): EmployeeAppraisalValues {
  const letterType = LETTER_TYPES.includes(detail.letterType as AppraisalLetterType)
    ? detail.letterType as AppraisalLetterType
    : "APPRAISAL";
  const designation = detail.promotedDesignation || detail.designationChangeText || detail.designation || "";
  const designationId = detail.promotedDesignationId ?? detail.designationId;

  return {
    employeeId: String(detail.employeeId),
    letterType,
    letterIssueDate: localDate(detail.letterIssueDate),
    effectiveDate: localDate(detail.effectiveDate),
    noticePeriod: noticePeriodValue(detail.noticePeriod),
    departmentId: detail.promotedDepartmentId != null ? String(detail.promotedDepartmentId) : "",
    designationId: designationId != null ? String(designationId) : "",
    designation,
    annualComponent: detail.annualComponent?.enabled ? "YES" : "NO",
    annualComponentRemarks: detail.annualComponent?.remarks || "",
  };
}

export function appraisalAnnexureValuesFromDetail(detail: AppraisalDetail): AppraisalAnnexureValues {
  const basic = amount(detail.compensation?.basic);
  const automaticPf = providentFundFromBasic(basic);
  const pfEmployeePart = amount(detail.compensation?.pfEmployeePart ?? automaticPf);
  const specialAllowance = amount(detail.allowances?.specialAllowance);
  const houseRentAllowance = amount(detail.allowances?.houseRentAllowance);
  const conveyanceAllowance = amount(detail.allowances?.conveyanceAllowance);
  const helperAndDriver = amount(detail.allowances?.helperAndDriver);
  const lta = amount(detail.allowances?.lta);
  const mealReimbursement = amount(detail.allowances?.mealReimbursement);
  const internetReimbursement = amount(detail.allowances?.internetReimbursement);
  const gymHealthClubReimbursement = amount(detail.allowances?.gymHealthClubReimbursement);
  const gratuity = amount(detail.statutory?.gratuity);
  const pfEmployerPart = amount(detail.statutory?.pfEmployerPart ?? automaticPf);
  const annualComponentAmount = detail.annualComponent?.enabled ? amount(detail.annualComponent.amount) : 0;
  const monthlyGross = detail.baseValue == null
    ? basic + pfEmployeePart + specialAllowance + houseRentAllowance
      + conveyanceAllowance + helperAndDriver + lta + mealReimbursement
      + internetReimbursement + gymHealthClubReimbursement
    : amount(detail.baseValue);
  const esicEligible = isEsicEligible(monthlyGross);
  const esicEmployeePart = esicEligible ? esicEmployeeFromBasic(basic) : 0;
  const esicEmployerPart = esicEligible ? esicEmployerFromBasic(basic) : 0;
  const annualCtc = monthlyGross * 12 + annualBenefitsFromBasic(basic, pfEmployerPart, esicEmployerPart) + annualComponentAmount;

  return {
    annualCtc: amountText(annualCtc),
    monthlyGross: amountText(monthlyGross),
    basic: amountText(basic),
    pfEmployeePart: amountText(pfEmployeePart),
    esicEmployeePart: amountText(esicEmployeePart),
    specialAllowance: amountText(specialAllowance),
    houseRentAllowance: amountText(houseRentAllowance),
    conveyanceAllowance: amountText(conveyanceAllowance),
    helperAndDriver: amountText(helperAndDriver),
    lta: amountText(lta),
    mealReimbursement: amountText(mealReimbursement),
    internetReimbursement: amountText(internetReimbursement),
    gymHealthClubReimbursement: amountText(gymHealthClubReimbursement),
    gratuity: amountText(gratuity),
    pfEmployerPart: amountText(pfEmployerPart),
    esicEmployerPart: amountText(esicEmployerPart),
    annualComponentEnabled: Boolean(detail.annualComponent?.enabled),
    annualComponentAmount: amountText(annualComponentAmount),
    annualComponentRemarks: detail.annualComponent?.remarks || "",
    remarks: detail.remarks || "",
  };
}
